import React, { useMemo, useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowLeft, MapPin, Users, Sparkles, Calendar } from "lucide-react";
import { EVENTS } from "../data/events";
import { getEventDetail } from "../data/eventDetails";
import { Nav } from "./Nav";
import { Footer } from "./Footer";
import { SEO } from "./SEO";

const EASE = [0.76, 0, 0.24, 1];

const LineMask = ({ children, delay = 0 }) => (
  <span className="line-mask">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const EventDetailPage = () => {
  const { id } = useParams();
  const event = useMemo(() => EVENTS.find((e) => e.id === id), [id]);
  const detail = useMemo(() => getEventDetail(id), [id]);
  const related = useMemo(
    () =>
      EVENTS.filter((e) => e.category === event?.category && e.id !== id).slice(
        0,
        3,
      ),
    [event, id],
  );

  const { scrollY } = useScroll();
  const heroImgY = useTransform(scrollY, [0, 800], ["0%", "18%"]);
  const heroImgScale = useTransform(scrollY, [0, 800], [1.05, 1.15]);
  // Discrete on/off reveal for the "Return to the Season" arrow: hidden until
  // scrollY > 160, then snaps to visible. Also disables pointer-events while
  // hidden so it never intercepts clicks over the hero content.
  const [backArrowVisible, setBackArrowVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setBackArrowVisible(window.scrollY > 160);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!event) return <Navigate to="/" replace />;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: event.title,
    startDate: `${event.dateISO}T19:30:00+00:00`,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: detail?.address?.venue || event.location,
      address: detail?.address
        ? `${detail.address.street}, ${detail.address.city}`
        : event.location,
    },
    image: [event.image],
    description: detail?.description?.[0] || event.tagline,
    organizer: { "@type": "Organization", name: event.host },
    offers: (detail?.tickets || []).map((t) => ({
      "@type": "Offer",
      name: t.tier,
      price: t.price,
      priceCurrency: "GBP",
      availability: "https://schema.org/InStock",
    })),
  };

  return (
    <div
      data-testid="event-detail-page"
      className="relative bg-[color:var(--bt-bg)] text-[color:var(--bt-ink)] min-h-screen overflow-x-hidden"
    >
      <SEO
        title={event.title}
        description={detail?.description?.[0] || event.tagline}
        image={event.image}
        path={`/event/${event.id}`}
        type="article"
        jsonLd={jsonLd}
      />
      <div className="bt-grain" aria-hidden />
      <Nav />

      {/* Sticky "Return to the Season" - hidden until user scrolls past 160px */}
      <motion.div
        animate={{ opacity: backArrowVisible ? 1 : 0 }}
        transition={{ duration: 0.2 }}
        className={`fixed top-20 md:top-24 left-6 md:left-10 z-[9998] ${
          backArrowVisible ? "pointer-events-auto" : "pointer-events-none"
        }`}
      >
        <Link
          to="/"
          data-testid="event-back-link"
          className="inline-flex items-center gap-3 px-4 py-2 bg-[color:var(--bt-bg)]/85 backdrop-blur-md border border-[color:var(--bt-gold-line)] text-[color:var(--bt-ink-dim)] hover:text-[color:var(--bt-gold)] hover:border-[color:var(--bt-gold)] transition-colors"
        >
          <ArrowLeft size={14} />
          <span className="text-[11px] tracking-widest-brand uppercase">
            Return to the Season
          </span>
        </Link>
      </motion.div>

      {/* Hero */}
      <section
        data-testid="event-hero"
        className="relative h-[92svh] w-full overflow-hidden"
      >
        <motion.div
          style={{ y: heroImgY, scale: heroImgScale }}
          className="absolute inset-0 will-change-transform"
        >
          <img
            src={event.image}
            alt={event.title}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[color:var(--bt-bg)]/70 via-[color:var(--bt-bg)]/30 to-[color:var(--bt-bg)]" />
        </motion.div>

        {/* Frame */}
        <div className="pointer-events-none absolute inset-y-0 left-6 md:left-10 w-px bg-[color:var(--bt-gold-line)]" />
        <div className="pointer-events-none absolute inset-y-0 right-6 md:right-10 w-px bg-[color:var(--bt-gold-line)]" />

        <div className="relative z-10 h-full px-6 md:px-16 pt-32 pb-10 flex flex-col justify-end">
          <div className="max-w-[1500px]">
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <div className="h-px w-10 bg-[color:var(--bt-gold)]" />
              <span
                data-testid="event-category"
                className="text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-gold)]"
              >
                {event.categoryLabel} · Year {detail?.year || "MMXXVI"}
              </span>
            </div>

            <h1
              data-testid="event-title"
              className="font-serif-display text-[13vw] md:text-[9vw] leading-[0.9] tracking-tight text-[color:var(--bt-ink)]"
            >
              <LineMask delay={0.15}>{event.title}</LineMask>
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
              className="mt-8 md:mt-12 flex flex-wrap items-center gap-x-10 gap-y-4 text-[color:var(--bt-ink-dim)]"
            >
              <MetaItem icon={<Calendar size={14} />} label={event.date} testId="event-date" />
              <MetaItem icon={<MapPin size={14} />} label={event.location} testId="event-location" />
              {detail?.dressCode && (
                <MetaItem
                  icon={<Sparkles size={14} />}
                  label={detail.dressCode}
                  testId="event-dress-code"
                />
              )}
              {detail?.capacity && (
                <MetaItem
                  icon={<Users size={14} />}
                  label={detail.capacity}
                  testId="event-capacity"
                />
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Body - two column */}
      <section className="relative bg-[color:var(--bt-bg)] px-6 md:px-10 py-24 md:py-32">
        <div className="mx-auto max-w-[1500px] grid md:grid-cols-12 gap-10 md:gap-16">
          {/* Left - editorial content */}
          <div className="md:col-span-8 space-y-24">
            {/* 01 The Evening */}
            <Chapter
              number="01"
              kicker="The Evening"
              title="An evening, considered."
              testId="event-chapter-evening"
            >
              {(detail?.description || [event.tagline]).map((p, i) => (
                <p
                  key={i}
                  className="text-[color:var(--bt-ink-dim)] text-base md:text-lg leading-relaxed mb-4"
                >
                  {p}
                </p>
              ))}
            </Chapter>

            {/* 02 The Programme */}
            {detail?.programme && (
              <Chapter
                number="02"
                kicker="The Programme"
                title="The order of the evening."
                testId="event-chapter-programme"
              >
                <ol
                  data-testid="event-programme"
                  className="divide-y divide-[color:var(--bt-gold-line)] border-y border-[color:var(--bt-gold-line)]"
                >
                  {detail.programme.map((p, i) => (
                    <li
                      key={i}
                      className="flex items-baseline gap-8 py-5 md:py-6"
                    >
                      <span className="font-serif-display italic text-[color:var(--bt-gold)] text-2xl md:text-3xl w-24">
                        {p.time}
                      </span>
                      <span className="flex-1 font-serif-display text-[color:var(--bt-ink)] text-xl md:text-2xl leading-tight">
                        {p.title}
                      </span>
                      <span className="hidden md:inline text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)]">
                        Chapter {String(i + 1).padStart(2, "0")}
                      </span>
                    </li>
                  ))}
                </ol>
              </Chapter>
            )}

            {/* 03 The Host */}
            {detail?.hostBio && (
              <Chapter
                number="03"
                kicker="The Host"
                title={event.host}
                testId="event-chapter-host"
              >
                <p className="text-[color:var(--bt-ink-dim)] text-base md:text-lg leading-relaxed">
                  {detail.hostBio}
                </p>
              </Chapter>
            )}

            {/* 04 The Attire */}
            <Chapter
              number="04"
              kicker="The Attire"
              title="As the evening dictates."
              testId="event-chapter-attire"
            >
              <div className="border-t border-b border-[color:var(--bt-gold-line)] py-8 md:py-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                <div>
                  <div className="text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)] mb-3">
                    Dress code
                  </div>
                  <div
                    data-testid="attire-dress-code"
                    className="font-serif-display text-4xl md:text-6xl leading-none tracking-tight text-[color:var(--bt-ink)]"
                  >
                    {detail?.dressCode || "Black Tie"}
                  </div>
                </div>
                <p className="text-[color:var(--bt-ink-dim)] text-sm md:text-base leading-relaxed md:max-w-sm">
                  Should your wardrobe be otherwise engaged, our concierge
                  keeps a standing arrangement with a small number of houses
                  who dress our guests with quiet priority.
                </p>
              </div>

              <ConciergeLink
                href="https://www.mossbroshire.co.uk/occasion/black-tie"
                testId="attire-rental-link"
                label="Curated Evening Wear Rental"
                partner="Moss · Formal Hire"
              />
            </Chapter>

            {/* 05 Finishing Touches - three curated lifestyle notes */}
            <Chapter
              number="05"
              kicker="Finishing Touches"
              title="The particulars, quietly arranged."
              testId="event-chapter-finishing-touches"
            >
              <p className="text-[color:var(--bt-ink-dim)] text-base md:text-lg leading-relaxed mb-12 max-w-2xl">
                A short list of houses our editors quietly return to - the
                smallest details of the evening, considered and delivered
                without ceremony.
              </p>
              <div data-testid="finishing-touches" className="flex flex-col">
                <LifestyleNote
                  testId="lifestyle-heel"
                  index="i"
                  title="The Power of the Heel"
                  body="An evening is carried in the walk. For the heeled sandal that holds a room from arrival to the last reel, our editors send guests directly to the Selfridges shoe salon."
                  partner="Selfridges, London - Shoe Galleries"
                  href="https://www.selfridges.com/GB/en/cat/shoes/womens/sandals/heeled-sandals/high-heel/?pn=2"
                />
                <LifestyleNote
                  testId="lifestyle-lingerie"
                  index="ii"
                  title="La Dame de France"
                  body="Luxury lingerie in the French manner - the quiet foundation beneath the gown. La Dame de France dress our guests from the inside out, with a discretion we rather admire."
                  partner="La Dame de France - Luxury Lingerie"
                  href="https://ladamedefrance.com"
                />
                <LifestyleNote
                  testId="lifestyle-hosiery"
                  index="iii"
                  title="Sublimate Legs - The Power of Hosiery"
                  body="Seamed silk, a fully-fashioned welt, the faint shimmer of a well-chosen stocking. Cervin have made hosiery in France since 1919, and remain the only house we recommend by name."
                  partner="Cervin, Paris - Fine Hosiery since 1919"
                  href="https://www.cervin-store.com"
                />
              </div>
            </Chapter>

            {/* 06 The Address */}
            {detail?.address && (
              <Chapter
                number="06"
                kicker="The Address"
                title={detail.address.venue}
                testId="event-chapter-address"
              >
                <div className="text-[color:var(--bt-ink)] font-serif-display text-xl md:text-2xl leading-relaxed">
                  {detail.address.street}
                  <br />
                  {detail.address.city}
                </div>
                <p className="mt-4 text-[color:var(--bt-ink-dim)] text-sm md:text-base leading-relaxed">
                  {detail.address.note}
                </p>
              </Chapter>
            )}

            {/* 07 The Gallery */}
            {detail?.gallery && (
              <Chapter
                number="07"
                kicker="The Gallery"
                title="From previous years."
                testId="event-chapter-gallery"
              >
                <div
                  data-testid="event-gallery"
                  className="grid grid-cols-2 md:grid-cols-2 gap-3 md:gap-4"
                >
                  {detail.gallery.map((src, i) => (
                    <div
                      key={i}
                      className="relative overflow-hidden aspect-[4/5] border border-[color:var(--bt-gold-line)] spotlight-frame group"
                    >
                      <img
                        src={src}
                        alt={`${event.title} archive ${i + 1}`}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
                      />
                      <div className="absolute top-3 left-3 text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-gold)]">
                        Plate {String(i + 1).padStart(2, "0")}
                      </div>
                    </div>
                  ))}
                </div>
              </Chapter>
            )}
          </div>

          {/* Right - sticky booking card */}
          <aside className="md:col-span-4">
            <div
              data-testid="event-booking-card"
              className="md:sticky md:top-32 border border-[color:var(--bt-gold-line)] bg-[color:var(--bt-bg-2)]/70 backdrop-blur-xl"
            >
              <div className="p-8 border-b border-[color:var(--bt-gold-line)]">
                <div className="text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-gold)] mb-3">
                  - {event.date}
                </div>
                <div className="font-serif-display text-3xl leading-tight text-[color:var(--bt-ink)]">
                  {event.title}
                </div>
                <div className="mt-3 text-sm text-[color:var(--bt-ink-dim)]">
                  Hosted by {event.host}
                </div>
              </div>

              {detail?.tickets && (
                <div className="p-8 border-b border-[color:var(--bt-gold-line)]">
                  <div className="text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)] mb-5">
                    Tables & Seats
                  </div>
                  <ul className="space-y-4" data-testid="event-tickets">
                    {detail.tickets.map((t, i) => (
                      <li
                        key={i}
                        className="flex items-baseline justify-between gap-4"
                      >
                        <span className="text-[color:var(--bt-ink)] text-sm">
                          {t.tier}
                        </span>
                        <span className="font-serif-display italic text-xl text-[color:var(--bt-gold)]">
                          {t.price}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="p-8 space-y-4">
                <button
                  data-testid="event-enquire-button"
                  className="bt-btn w-full"
                >
                  Enquire · By Invitation <ArrowUpRight size={14} />
                </button>
                <button
                  data-testid="event-add-to-list-button"
                  className="bt-btn-ghost w-full"
                >
                  Add to My List
                </button>
                <p className="text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)] pt-2">
                  Correspondence acknowledged within 48 hours.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Related events */}
      {related.length > 0 && (
        <section
          data-testid="event-related-section"
          className="relative bg-[color:var(--bt-bg-2)] border-y border-[color:var(--bt-gold-line)] px-6 md:px-10 py-24 md:py-32"
        >
          <div className="mx-auto max-w-[1500px]">
            <div className="flex items-center gap-4 mb-10">
              <div className="h-px w-10 bg-[color:var(--bt-gold)]" />
              <span className="text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-gold)]">
                Also this season
              </span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-[0.95] tracking-tight text-[color:var(--bt-ink)] mb-12">
              You may also{" "}
              <span className="italic font-light text-[color:var(--bt-gold)]">
                consider.
              </span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r, i) => (
                <Link
                  key={r.id}
                  to={`/event/${r.id}`}
                  data-testid={`related-event-${r.id}`}
                  className="group block border border-[color:var(--bt-gold-line)] hover:border-[color:var(--bt-gold)] transition-colors"
                >
                  <div className="relative overflow-hidden h-[42vh] spotlight-frame">
                    <img
                      src={r.image}
                      alt={r.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--bt-bg)] via-[color:var(--bt-bg)]/20 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-6">
                      <div className="text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-gold)]">
                        {r.date}
                      </div>
                      <div className="mt-2 font-serif-display text-2xl text-[color:var(--bt-ink)]">
                        {r.title}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

const MetaItem = ({ icon, label, testId }) => (
  <span
    data-testid={testId}
    className="flex items-center gap-2 text-[11px] tracking-widest-brand uppercase"
  >
    <span className="text-[color:var(--bt-gold)]">{icon}</span>
    {label}
  </span>
);

const Chapter = ({ number, kicker, title, testId, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.9, ease: EASE }}
    data-testid={testId}
  >
    <div className="flex items-baseline gap-6 md:gap-8 mb-8">
      <span className="font-serif-display italic text-[color:var(--bt-gold)] text-4xl md:text-5xl">
        {number}
      </span>
      <div>
        <div className="text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)] mb-2">
          - {kicker}
        </div>
        <h2 className="font-serif-display text-3xl md:text-5xl leading-[1] tracking-tight text-[color:var(--bt-ink)]">
          {title}
        </h2>
      </div>
    </div>
    <div className="md:pl-16">{children}</div>
  </motion.div>
);

/**
 * ConciergeLink - inline horizontal link with hairline, editorial-only styling.
 * Used inside The Attire chapter for the primary rental partner.
 */
const ConciergeLink = ({ href, testId, label, partner }) => (
  <a
    href={href}
    data-testid={testId}
    target="_blank"
    rel="noopener noreferrer"
    className="group mt-8 flex flex-col md:flex-row md:items-baseline md:justify-between gap-3 md:gap-6 py-6 border-b border-[color:var(--bt-gold-line)] hover:border-[color:var(--bt-gold)] transition-colors"
  >
    <div className="flex-1">
      <div className="text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)] mb-2">
        Concierge - {label}
      </div>
      <div className="font-serif-display italic text-2xl md:text-3xl text-[color:var(--bt-ink)] group-hover:text-[color:var(--bt-gold)] transition-colors">
        {partner}
      </div>
    </div>
    <div className="flex items-center gap-3 text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-gold)]">
      <span className="w-10 h-px bg-[color:var(--bt-gold)] transition-all duration-500 group-hover:w-20" />
      Arrange
      <ArrowUpRight size={14} />
    </div>
  </a>
);

/**
 * LifestyleNote - an editorial "section" within Finishing Touches. Roman
 * numeral, title, a short considered paragraph, and a single concierge link.
 * Minimal, premium, no imagery.
 */
const LifestyleNote = ({ index, title, body, partner, href, testId }) => (
  <a
    href={href}
    data-testid={testId}
    target="_blank"
    rel="noopener noreferrer"
    className="group grid md:grid-cols-12 gap-4 md:gap-8 py-10 border-t border-[color:var(--bt-gold-line)] last:border-b hover:bg-[color:var(--bt-gold-glow)] transition-colors"
  >
    <div className="md:col-span-1 font-serif-display italic text-[color:var(--bt-gold)] text-2xl md:text-3xl leading-none">
      {index}.
    </div>
    <div className="md:col-span-6">
      <h3 className="font-serif-display text-2xl md:text-4xl leading-tight tracking-tight text-[color:var(--bt-ink)] group-hover:text-[color:var(--bt-gold)] transition-colors">
        {title}
      </h3>
      <p className="mt-4 text-[color:var(--bt-ink-dim)] text-base md:text-lg leading-relaxed max-w-xl">
        {body}
      </p>
    </div>
    <div className="md:col-span-5 flex md:flex-col md:items-end md:justify-between gap-4">
      <span className="text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)] md:text-right">
        {partner}
      </span>
      <span className="flex items-center gap-3 text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-gold)] whitespace-nowrap">
        <span className="w-8 h-px bg-[color:var(--bt-gold)] transition-all duration-500 group-hover:w-14" />
        Discover
        <ArrowUpRight size={14} />
      </span>
    </div>
  </a>
);

export default EventDetailPage;
